/**
 * PlateLane - what a kill-team brother's plate draws in its lane (Squad Mode stage 8): his life signs in Solo Mode,
 * the B-scope in Squad Mode (plan modes-rework; the target panel's the calm one, never pulsing). See usePlateLane.
 */
import useFearMoment from '../../useFearMoment';
import usePsykerMoment from '../../usePsykerMoment';
import { useMemo } from 'react';
import { lifeSignsOf } from '../../../../engine/plate/lifeSigns';
import { bscopeOf } from '../../../../engine/plate/bscope';
import { plateBand } from '../../../../engine/plate/plateBand';
import type { Target } from '../../../../types';
import type { PlateLaneOf } from '../../usePlateLane';
import LifeSigns from './LifeSigns';
import BScope from './BScope';

interface Props {
  u: string;
  of: PlateLaneOf;
  target: Target;
  conditions: readonly string[];
  dead: boolean;
  side: 'actor' | 'target';
  /** The two-second start is playing (an entry, a start, a join); never on the target panel. */
  starting: boolean;
}

/** His life signs, the passing moments included: a failed Fear test, a power used (a Force or Warp attack). */
function useSigns({ target, conditions, dead, of }: Props) {
  const band = plateBand(target, dead);
  const fear = useFearMoment(conditions);
  const psy = usePsykerMoment(of.id);
  const { criticalDamage, frenzied, psyRating } = target;
  return useMemo(
    () => lifeSignsOf({ band, conditions, criticalDamage, frenzied, moments: [...fear, ...psy], psyRating }),
    [band, conditions, criticalDamage, frenzied, fear, psy, psyRating],
  );
}

function Solo(props: Props) {
  const { u, of } = props;
  const signs = useSigns(props);
  return <LifeSigns u={u} lane={of.lane.lane} unit={of.lane.lane.w / of.lane.px} signs={signs} />;
}

function Squad(props: Props) {
  const { of, target, side, starting } = props;
  const signs = useSigns(props);
  const kt = of.k.teams.killTeams[target.teamTag ?? ''];
  const geometry = bscopeOf({
    lane: of.lane.lane,
    brothers: of.k.around?.(of.id) ?? [],
    rangeM: kt?.rangeM ?? 30,
    acting: side === 'actor',
  });
  const unit = of.lane.lane.w / of.lane.px;
  return (
    <>
      {/* The hand-over: his heart stays, slim and faint behind the scope; his mind gives way to the instrument. */}
      <LifeSigns u={`${props.u}-slim`} lane={of.lane.lane} unit={unit} signs={signs} slim />
      <BScope geometry={geometry} unit={unit} starting={starting && side === 'actor'} />
    </>
  );
}

export default function PlateLane(props: Props) {
  return props.of.kind === 'squad' ? <Squad {...props} /> : <Solo {...props} />;
}
